import React, { useState } from 'react';
import { Box, Card, CardHeader, CardBody, Heading, Text, Button, Flex, Icon, Badge, useToast, Divider, Textarea } from '@chakra-ui/react';
import { FiAlertCircle, FiSend, FiX, FiEdit2 } from 'react-icons/fi';
import FqPaClaimDetailShell from './FqPaClaimDetailShell';

const mockData = {
  type: '835 Denial',
  patient: { first_name: 'Linda', last_name: 'Martinez', member_id: 'UPMC77H304182' },
  provider: { name: 'Rachel Stein, OD', npi: '1558204773' },
  claim: {
    diagnoses: ['Z01.00'], // Encounter for examination of eyes and vision without abnormal findings
    procedures: [
      { cpt: '92004', modifiers: [], charge: 186.00 } // Eye exam billed alone \u2014 T1015 + U3 missing
    ],
    clinical_note: "Patient presents for comprehensive eye examination. Complains of blurry vision when reading. Refraction performed. Prescription updated. No signs of glaucoma or macular degeneration."
  }
};

const FqPaDemoAppealDraft = () => {
  const [isApproved, setIsApproved] = useState(false);
  const [appealText, setAppealText] = useState(`To Whom It May Concern,

We are resubmitting the corrected claim for patient Linda Martinez (ID: UPMC77H304182), denied with CARC 16 (claim/service lacks information or has billing error(s)).

The original submission reported CPT 92004 without the required FQHC encounter code. Per MA Bulletin 08-20-04 ("Vision Services Provided in the Federally Qualified Health Center and Rural Health Clinic"), vision encounters furnished by an optometrist are payable at the separate vision PPS encounter rate and must be billed as T1015 with pricing modifier U3, with the detail service reported as a zero-pay tracking line. Our center has a Medicaid FQHC Cost Report on file establishing the vision PPS rate.

The corrected claim (T1015-U3 on line 1; 92004 as a zero-pay tracking line) is attached. Please reprocess for payment at our vision PPS encounter rate.

Sincerely,
Billing Department`);
  const toast = useToast();

  const handleApprove = () => {
    setIsApproved(true);
    toast({
      title: "Appeal Sent",
      description: "Appeal letter and supporting documentation have been queued for submission.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  const rightPanel = (
    <Card borderColor="purple.200" borderWidth="2px" shadow="md">
      <CardHeader bg="purple.50" pb={4}>
        <Flex justify="space-between" align="center">
          <Heading size="md" display="flex" alignItems="center" color="purple.800">
            <Icon as={FiAlertCircle} mr={2} />
            Revedy Action Required
          </Heading>
          <Badge colorScheme="purple" variant="solid">Tier B</Badge>
        </Flex>
      </CardHeader>
      <CardBody>
        <Box mb={4}>
          <Text fontWeight="bold" color="red.600" mb={1}>Denial Reason (CARC 16):</Text>
          <Text fontSize="sm">Claim/service lacks information or has billing error(s) which is needed for adjudication. (Missing T1015)</Text>
        </Box>
        
        <Divider my={4} />

        <Box mb={4}>
          <Text fontWeight="bold" color="purple.700" mb={1}>Revedy Analysis:</Text>
          <Text fontSize="sm" mb={2}>
            The denial is technically correct \u2014 but easily fixed. In PA, a vision encounter is a separately payable PPS encounter type, billed as T1015 with the U3 pricing modifier on the first claim line. The original claim omitted both, so it failed the encounter-code edit. Revedy has drafted the corrected claim and cover letter; no clinical dispute is needed.
          </Text>
        </Box>

        <Box mb={4} p={3} bg="gray.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="purple.400">
          <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={1}>Rule Citation:</Text>
          <Text fontSize="sm" fontStyle="italic">"To receive a separate vision PPS encounter rate... the U3 pricing modifier with the T1015 code must be on the claim." (OMAP MA Bulletin 08-20-04; vision encounters payable in addition to medical, dental and BH encounters on the same day)</Text>
        </Box>

        <Box mb={6}>
          <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={1}>Drafted Corrected-Claim Cover Letter:</Text>
          <Textarea 
            value={appealText}
            onChange={(e) => setAppealText(e.target.value)}
            size="sm"
            rows={10}
            isDisabled={isApproved}
          />
        </Box>

        <Flex gap={3}>
          <Button 
            flex="1" 
            colorScheme="purple" 
            leftIcon={<FiSend />} 
            onClick={handleApprove}
            isDisabled={isApproved}
          >
            {isApproved ? 'Sent' : 'Approve & Send'}
          </Button>
          <Button variant="ghost" colorScheme="red" isDisabled={isApproved}>
            <Icon as={FiX} />
          </Button>
        </Flex>
      </CardBody>
    </Card>
  );

  return <FqPaClaimDetailShell claimData={mockData} rightPanel={rightPanel} backTo="/fq-pa/835-review" />;
};

export default FqPaDemoAppealDraft;